import { useEffect, useState } from 'react';

/**
 * Neighborhood outlines, keyed by the same area names the events use.
 *
 * Fetched once per page load and shared by every map, via a module-level
 * promise — the callout mounts and unmounts constantly as the user browses, and
 * refetching 37KB each time would be wasteful.
 *
 * Every ring is a real boundary from the City of New York's 2020 Neighborhood
 * Tabulation Areas — the same official geometry Google Maps draws. Nothing here
 * is approximated.
 */

export interface AreaShape {
  /** [lng, lat] pairs forming a closed ring. */
  ring: [number, number][];
  source: 'nyc-nta';
  /** How many official NTAs were unioned to make it. */
  parts: number;
}

export type AreaShapes = Record<string, AreaShape | null>;

const URL_PATH = 'data/areas.json';
let cached: Promise<AreaShapes> | null = null;

function load(): Promise<AreaShapes> {
  if (!cached) {
    cached = fetch(new URL(URL_PATH, document.baseURI).toString())
      .then((r) => (r.ok ? (r.json() as Promise<AreaShapes>) : {}))
      .catch(() => ({}));
  }
  return cached;
}

export function useAreaShapes(): AreaShapes {
  const [shapes, setShapes] = useState<AreaShapes>({});
  useEffect(() => {
    let live = true;
    void load().then((s) => {
      if (live) setShapes(s);
    });
    return () => {
      live = false;
    };
  }, []);
  return shapes;
}
